/* eslint-disable react/no-unescaped-entities */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap, Sparkles, Gift, TrendingUp, X, ArrowRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, ASSETS } from "@/lib/assets";

const BENEFITS = [
  { icon: Gift, title: "Exclusive Fifi merch", desc: "Hoodies, tote bags, stickers — only for ambassadors." },
  { icon: Sparkles, title: "Bonus XP + early tiers", desc: "Fast-track Fifi to Analyst Cat & unlock premium drops." },
  { icon: TrendingUp, title: "Cash & referral payouts", desc: "Earn per verified signup from your campus." },
  { icon: Users, title: "Community + resume boost", desc: "Join a Slack of student ambassadors + real fintech marketing experience." },
];

const AmbassadorModal = ({ open, onClose }) => {
  const [form, setForm] = useState({
    full_name: "", email: "", university: "", year_of_study: "",
    country: "", instagram: "", tiktok: "", linkedin: "", why_join: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.why_join.length < 10) {
      toast.error("Please tell us a bit more about why you'd like to join (at least 10 characters).");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/ambassador`, form);
      setDone(true);
      toast.success("Application submitted! Check your inbox 🎓");
    } catch (err) {
      toast.error("Couldn't submit. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="ambassador-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="p-6 md:p-8 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#EDE9FE] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#5B21B6] mb-3">
              <GraduationCap size={12} /> Campus Ambassador
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-black text-slate-900 leading-tight">
              {done ? "You're in! 🎓" : "Apply to represent FinX on your campus"}
            </h2>
            {!done && (
              <p className="text-sm text-slate-600 mt-2 max-w-md">
                Rolling review — we reply within 5-7 days. All fields marked * are required.
              </p>
            )}
          </div>
          <button
            data-testid="ambassador-modal-close"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
          >
            <X size={20} />
          </button>
        </div>

        {done ? (
          <div className="flex-1 overflow-y-auto p-8 text-center">
            <img src={ASSETS.fifiCheer || ASSETS.fifiHero} alt="Fifi cheering" className="w-40 mx-auto mb-6" />
            <p className="text-slate-700 text-lg mb-2">Thanks, <b>{form.full_name.split(" ")[0]}</b>!</p>
            <p className="text-slate-600">
              We've sent a confirmation to <b>{form.email}</b>. In the meantime,{" "}
              <a href="https://apps.apple.com/us/app/finx-stocks/id6783580699" target="_blank" rel="noopener noreferrer" className="text-[#5B21B6] font-bold underline">download FinX on iOS</a>{" "}
              and start earning XP.
            </p>
            <Button onClick={onClose} className="mt-8 rounded-full brand-gradient text-white font-bold border-0 px-8 h-11">Done</Button>
          </div>
        ) : (
          <form data-testid="ambassador-form" onSubmit={submit} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Full name *</span>
                <Input required data-testid="amb-name" value={form.full_name} onChange={update("full_name")} placeholder="Jane Doe" className="mt-1.5 h-11 rounded-xl" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Email *</span>
                <Input required type="email" data-testid="amb-email" value={form.email} onChange={update("email")} placeholder="jane@university.edu" className="mt-1.5 h-11 rounded-xl" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">University *</span>
                <Input required data-testid="amb-university" value={form.university} onChange={update("university")} placeholder="Stanford University" className="mt-1.5 h-11 rounded-xl" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Year of study *</span>
                <Input required data-testid="amb-year" value={form.year_of_study} onChange={update("year_of_study")} placeholder="Sophomore / Junior / Master's" className="mt-1.5 h-11 rounded-xl" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Country / Region</span>
                <Input data-testid="amb-country" value={form.country} onChange={update("country")} placeholder="United States" className="mt-1.5 h-11 rounded-xl" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Instagram handle *</span>
                <Input required data-testid="amb-ig" value={form.instagram} onChange={update("instagram")} placeholder="@yourhandle" className="mt-1.5 h-11 rounded-xl" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">TikTok handle</span>
                <Input data-testid="amb-tiktok" value={form.tiktok} onChange={update("tiktok")} placeholder="@yourhandle" className="mt-1.5 h-11 rounded-xl" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">LinkedIn URL</span>
                <Input data-testid="amb-linkedin" value={form.linkedin} onChange={update("linkedin")} placeholder="https://linkedin.com/in/..." className="mt-1.5 h-11 rounded-xl" />
              </label>
            </div>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Why do you want to be a FinX Ambassador? *
              </span>
              <textarea
                required
                data-testid="amb-why"
                value={form.why_join}
                onChange={update("why_join")}
                rows={4}
                placeholder="Tell us about your student community, interests, and how you'd champion FinX on campus."
                className="mt-1.5 w-full rounded-xl border-2 border-slate-200 focus:border-[#A855F7] focus:ring-0 focus:outline-none p-3 text-sm"
              />
            </label>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                type="submit"
                data-testid="amb-submit"
                disabled={loading}
                className="flex-1 rounded-full h-12 font-bold text-white brand-gradient border-0 disabled:opacity-70"
              >
                {loading ? "Submitting..." : "Submit Application"}
                {!loading && <ArrowRight size={16} className="ml-1.5" />}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const AmbassadorSection = ({ open, onOpen, onClose }) => {
  return (
    <>
      <section
        id="ambassadors"
        data-testid="ambassador-section"
        className="relative py-20 md:py-24 overflow-hidden"
      >
        <div className="absolute inset-0 -z-10 brand-gradient-soft opacity-50" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="relative rounded-3xl brand-gradient p-8 md:p-14 text-white overflow-hidden shadow-2xl">
            <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{
              backgroundImage: 'radial-gradient(circle at 15% 30%, white 1px, transparent 1px), radial-gradient(circle at 75% 65%, white 1.5px, transparent 1.5px)',
              backgroundSize: '48px 48px, 36px 36px'
            }} />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em]">
                  <GraduationCap size={14} /> Applications Open
                </div>
                <h2 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
                  Be a FinX
                  <br />
                  Campus Ambassador.
                </h2>
                <p className="mt-6 text-lg text-white/90 max-w-xl">
                  Bring FinX to your university. Host events, share Fifi across your community,
                  and earn XP, exclusive merch, and cash payouts as your campus grows.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button
                    data-testid="ambassador-apply-btn"
                    onClick={onOpen}
                    className="rounded-full bg-slate-900 hover:bg-black text-white font-bold h-12 px-7 border-0"
                  >
                    Apply Now <ArrowRight size={16} className="ml-1.5" />
                  </Button>
                  <Button
                    data-testid="ambassador-learn-btn"
                    variant="outline"
                    onClick={() => document.getElementById("meet-fifi")?.scrollIntoView({ behavior: "smooth" })}
                    className="rounded-full bg-white/10 hover:bg-white/20 text-white font-bold h-12 px-7 border-white/30 hover:border-white/50 backdrop-blur"
                  >
                    Meet Fifi first
                  </Button>
                </div>
              </div>
              <div className="lg:col-span-5 grid grid-cols-2 gap-3">
                {BENEFITS.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div
                      key={b.title}
                      data-testid={`amb-benefit-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-4"
                    >
                      <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center mb-3">
                        <Icon size={16} />
                      </div>
                      <h4 className="font-display font-bold text-sm leading-tight">{b.title}</h4>
                      <p className="text-xs text-white/80 mt-1 leading-snug">{b.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <AmbassadorModal open={open} onClose={onClose} />
    </>
  );
};

export default AmbassadorSection;
